import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';

/**
 * The prominent feature image (overview §3) + the "Talk to us" showcase images:
 * the professional's own featured projects, falling back to associated projects
 * (the most popular from their projects tab) when they have none — e.g. product
 * suppliers like Resene. Always projects, since a single large image reads best
 * as a project. Only items with a lead image are kept, in display order.
 */
export const getFeaturedWork = (
    ownProjects: ProjectTileViewModel[] | undefined,
    associatedProjects: ProjectTileViewModel[] | undefined,
): ProjectTileViewModel[] => {
    const own = (ownProjects ?? []).filter((p) => p.images?.[0]?.src);
    if (own.length > 0) return own;
    return (associatedProjects ?? []).filter((p) => p.images?.[0]?.src);
};

export type ShowcaseItem = ProjectTileViewModel | ProductTileViewModel;

interface SelectedShowcase {
    /** True when we fell back to products — callers relabel Projects → Products. */
    usingProducts: boolean;
    items: ShowcaseItem[];
}

/**
 * Items for the "(01 — Selected work)" grid: the professional's own featured
 * projects, falling back to featured **products** for product suppliers (e.g.
 * Resene) that have no projects — that grid reads best showcasing what they
 * actually sell. Only items with a lead image are kept, in display order.
 */
export const getSelectedShowcase = (
    ownProjects: ProjectTileViewModel[] | undefined,
    products: ProductTileViewModel[] | undefined,
): SelectedShowcase => {
    const projects = (ownProjects ?? []).filter((p) => p.images?.[0]?.src);
    if (projects.length > 0) {
        return { usingProducts: false, items: projects };
    }
    const productItems = (products ?? []).filter((p) => p.images?.[0]?.src);
    return { usingProducts: productItems.length > 0, items: productItems };
};
